import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/react-hooks';
import {
  Modal, ModalBody, Button, Form, FormGroup, Label, Input,
} from 'reactstrap';
import { map } from 'lodash';
import { useToasts } from 'react-toast-notifications';
import VideoThumbnail from 'react-video-thumbnail';
import copyPostUrl from 'copy-to-clipboard';
import {
  handleGraphqlError, MEDIA_VIEW_TYPE, staticConstants, getTimeAgoFormat, RenderRequestPopup, RenderSocialIcons, Messages,
} from '../../utils';
import { LOADER_MUTATION, UPDATE_BETA_POST_REQUEST } from '../../graphql-client/mutations';
import MediaPopup from './MediaPopup';

function BetaPostRequest(props) {
  const {
    isOpen, onClose, displayUser, segmentId, offerId, shareType, updateOfferDetail,
  } = props;
  const { addToast } = useToasts();
  const [modal, setModal] = useState(isOpen);
  const [rejectReasonIdx, setRejectReasonIdx] = useState(-1);
  const [rejectReason, setRejectReason] = useState('');
  const [otherReasonValue, setOtherReasonValue] = useState('');
  const [mediaPopup, setMediaPopup] = useState(false);
  const [mediaItem, setMediaItem] = useState();
  const baseUrl = window.location.host;

  const toggle = () => {
    setModal(!modal);
    onClose();
  };

  /* function to close the publish post modal */
  const toggleMediaModal = () => {
    setMediaPopup(!mediaPopup);
  };

  const hideModal = () => {
    toggle();
    onClose();
  };

  const [loaderMutate] = useMutation(LOADER_MUTATION, {
    variables: { show: false },
  });

  const [updateArtistStatus] = useMutation(UPDATE_BETA_POST_REQUEST, {
    variables: {},
    onError(err) {
      loaderMutate({ variables: { show: false } });
      addToast(handleGraphqlError(err), { appearance: 'error' });
    },

    onCompleted(status) {
      loaderMutate({ variables: { show: false } });
      updateOfferDetail();
      onClose();
      addToast(status.updateBetaPostRequest.message, { appearance: 'success' });
    },
  });

  const toggleBetaPostRejectReason = (defaultReason, selectedIndex) => {
    setRejectReason(rejectReasonIdx === selectedIndex ? '' : defaultReason);
    setRejectReasonIdx(rejectReasonIdx === selectedIndex ? -1 : selectedIndex);
  };

  const renderBetaPostRejectReason = () => {
    const artistReasonList = staticConstants.ARTIST_REQUEST_REJECT_REASON;
    return map(artistReasonList, (ele, index) => ele && (
      <div key={`reason-${index}`} onClick={() => { toggleBetaPostRejectReason(ele.label, index); }}>
        <label className="reason-col" htmlFor={`reason${index + 1}`}>
          <input
            key={index}
            type="checkbox"
            checked={ele.value === (rejectReasonIdx + 1)}
          />
          <span>{ele.label}</span>
        </label>
      </div>
    ));
  };

  const actionOnArtistRequest = (reqObj) => {
    if (reqObj.status === staticConstants.STATUS_TYPE.REJECTED) {
      reqObj.rejectReason = {
        preDefined: rejectReason,
        custom: otherReasonValue,
      };
    }
    loaderMutate({ variables: { show: true } });
    updateArtistStatus({ variables: reqObj });
  };

  /* function to filter out the data based on the current status of the request and
    find the time ago calling getTimeAgoFormat function */
  const displayTimeAgo = (status, timeLine) => {
    const filteredData = timeLine && timeLine.length && timeLine.filter((timeline) => timeline.status === status);
    return getTimeAgoFormat(filteredData && filteredData[0].createdAt);
  };

  /* function called on click of media post to open image or video in modal
    timeline segment object passed as a variable */
  const openMediaPopup = (item) => {
    setMediaPopup(true);
    setMediaItem(item);
  };

  const renderMedia = (mediaObj, mediaFile) => {
    switch (mediaFile.mediaType) {
      case MEDIA_VIEW_TYPE.IMAGE:
        return mediaObj;
      case MEDIA_VIEW_TYPE.VIDEO:
        return (
          <>
            <VideoThumbnail
              videoUrl={mediaFile.url}
            />
          </>
        );
      default:
        break;
    }
    return null;
  };

  const handleCopyLink = () => {
    if (displayUser) {
      copyPostUrl(`${baseUrl}/${'betaPost'}/${segmentId}/${'userId'}/${displayUser.user._id}`);
      addToast(Messages.copyLinkSuccess, { appearance: 'success' });
    }
  };

  return (
    <Modal isOpen={modal} className="modal-request modal-beta-request modal-dialog-centered">
      <ModalBody>
        <div className="modal-request-account">
          <h2>
            Draft Post Request
            <div className="social-icons">
              {RenderSocialIcons(shareType).map((ele) => ele)}
            </div>
          </h2>
          <RenderRequestPopup
            displayUser={displayUser}
            displayTimeAgo={displayTimeAgo}
            mediaPopup={openMediaPopup}
            renderMedia={renderMedia}
          />
        </div>
        <Form className="modal-request-content">
          <span
            className="icon icon-cross"
            onClick={hideModal}
          />
          <h4 className="share-feedback-heading">
            Share your feedback
            <Button
              color="share"
              className="btn-copy"
              onClick={() => handleCopyLink()}
            >
              Copy link
            </Button>
          </h4>
          <div className="reason-col-wrap">
            <p className="reason-col head">
              <span>{staticConstants.REQUEST_MESSAGE}</span>
            </p>
          </div>
          {renderBetaPostRejectReason()}
          <FormGroup>
            <Label
              for="otherReason"
            >
              Other reason for rejection
            </Label>
            <Input
              type="textarea"
              className="medium"
              name="otherReason"
              value={otherReasonValue}
              onChange={(e) => setOtherReasonValue(e.target.value)}
            />
          </FormGroup>
          <div className="modal-request-btn-group">
            <Button
              color="reject"
              disabled={!otherReasonValue && !rejectReason}
              onClick={() => {
                actionOnArtistRequest({
                  offerId,
                  artistId: displayUser && displayUser.user._id,
                  segmentId,
                  status: staticConstants.STATUS_TYPE.REJECTED,
                });
              }}
            >
              Reject & notify
            </Button>
            <Button
              color="accept"
              onClick={() => {
                actionOnArtistRequest({
                  offerId,
                  artistId: displayUser && displayUser.user._id,
                  segmentId,
                  status: staticConstants.STATUS_TYPE.APPROVED,
                });
              }}
              disabled={otherReasonValue || rejectReason}
            >
              Approve draft post & notify
            </Button>
          </div>
        </Form>
        {mediaPopup && (
          <MediaPopup
            isOpen={mediaPopup}
            onClose={() => { toggleMediaModal(); }}
            timelineData={mediaItem}
          />
        )}
      </ModalBody>
    </Modal>
  );
}

BetaPostRequest.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  displayUser: PropTypes.object.isRequired,
  segmentId: PropTypes.string.isRequired,
  offerId: PropTypes.string.isRequired,
  shareType: PropTypes.object.isRequired,
  updateOfferDetail: PropTypes.func.isRequired,
  betaPostId: PropTypes.string.isRequired,
};

export default BetaPostRequest;
